/**
 * Live "current / total" counter for resource-list carousels.
 *
 * Scoped to the closest `.section-resource-list`. It only reads the section's own
 * carousel (`slideshow-slide.resource-list__slide`), never slideshows nested in cards
 * (e.g. the product card image gallery).
 */
class CarouselCounter extends HTMLElement {
  /** @type {MutationObserver | undefined} */
  #mutationObserver;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  /** @type {HTMLElement | null} */
  #section = null;
  #frame = 0;
  /** @type {number | undefined} */
  #retryTimer;
  /** @type {number | null} */
  #pendingIndex = null;

  connectedCallback() {
    this.#section = this.closest('.section-resource-list');
    if (!this.#section) return;

    this.current = this.querySelector('.carousel-counter__current');
    this.totalEl = this.querySelector('.carousel-counter__total');

    this.#section.addEventListener('slideshow:select', this.#onSelect);
    window.addEventListener('resize', this.#schedule, { passive: true });

    // Fallback for when the event is missed: slides flip aria-hidden as they scroll in/out.
    this.#mutationObserver = new MutationObserver(this.#schedule);
    this.#mutationObserver.observe(this.#section, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-hidden'],
    });

    // Layout changes (breakpoints, fonts, images) can swap which carousel is visible.
    if (typeof ResizeObserver !== 'undefined') {
      this.#resizeObserver = new ResizeObserver(this.#schedule);
      this.#resizeObserver.observe(this.#section);
    }

    this.#schedule();
    // The slideshow component may not be upgraded / laid out yet on first paint.
    this.#retryTimer = window.setTimeout(this.#schedule, 300);
    window.addEventListener('load', this.#schedule, { once: true });
  }

  disconnectedCallback() {
    this.#section?.removeEventListener('slideshow:select', this.#onSelect);
    window.removeEventListener('resize', this.#schedule);
    window.removeEventListener('load', this.#schedule);
    this.#mutationObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#retryTimer);
    this.#frame = 0;
  }

  /** @param {Event} event */
  #onSelect = (event) => {
    const carousel = this.#getCarousel();
    const target = /** @type {Element} */ (event.target);
    // Ignore events bubbling from nested slideshows (product card galleries).
    if (!carousel || target.closest('slideshow-component') !== carousel) return;

    const index = /** @type {CustomEvent} */ (event).detail?.index;
    this.#pendingIndex = Number.isInteger(index) ? index : null;
    this.#schedule();
  };

  #schedule = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  };

  /**
   * Finds the visible outer carousel of this section.
   * With carousel_on_mobile there are two (desktop hidden / mobile visible).
   * @returns {HTMLElement | null}
   */
  #getCarousel() {
    if (!this.#section) return null;

    for (const slideshow of this.#section.querySelectorAll('slideshow-component')) {
      // Skip slideshows nested inside a resource-list slide (card galleries).
      if (slideshow.parentElement?.closest('slideshow-slide.resource-list__slide')) continue;
      if (!slideshow.querySelector('slideshow-slide.resource-list__slide')) continue;
      if (slideshow.getClientRects().length === 0) continue;
      return /** @type {HTMLElement} */ (slideshow);
    }

    return null;
  }

  #update() {
    const carousel = this.#getCarousel();
    const slides = carousel
      ? Array.from(carousel.querySelectorAll('slideshow-slide.resource-list__slide')).filter(
          (slide) => slide.closest('slideshow-component') === carousel
        )
      : [];

    if (!carousel || slides.length === 0) {
      this.hidden = true;
      return;
    }

    this.hidden = false;
    const total = slides.length;
    if (this.totalEl) this.totalEl.textContent = String(total);

    let index = this.#pendingIndex;
    this.#pendingIndex = null;

    if (index === null) {
      const firstVisible = slides.findIndex((slide) => slide.getAttribute('aria-hidden') === 'false');
      index = firstVisible !== -1 ? firstVisible : Number.isInteger(carousel.current) ? carousel.current : 0;
    }

    const lastVisible = slides[total - 1]?.getAttribute('aria-hidden') === 'false';
    const value = lastVisible ? total : Math.min(Math.max(index, 0), total - 1) + 1;

    if (this.current) this.current.textContent = String(value);
  }
}

if (!customElements.get('carousel-counter')) {
  customElements.define('carousel-counter', CarouselCounter);
}
